import { AlertTriangle, Info } from "lucide-react";
import { Modal } from "../Modal.js";
import { useAlerts } from "../../lib/alerts.js";

/**
 * What the system is currently complaining about.
 *
 * The design's version renders a fixed list of three, two of them
 * congratulations -- "Solar array hit 5.8 kW peak output" among them -- with a
 * read/unread flag and a "Clear all". None of it survives: a dashboard that
 * invents good news is training its reader to stop looking at the bell.
 *
 * The entries are derived instead, in `lib/alerts.ts`, from the same stream
 * the page is already holding open. When the list is empty that is a real
 * statement about the system rather than an inbox that happens to be clear,
 * so it is worded as one.
 */
export function NotificationsModal({ onClose }: { onClose: () => void }) {
  const alerts = useAlerts();

  return (
    <Modal title="Notifications" onClose={onClose}>
      {alerts.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-sm text-ink">Nothing to report.</p>
          <p className="mt-1 text-xs text-ink-muted">
            Readings are arriving and are inside this instance&rsquo;s staleness window.
          </p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {alerts.map((alert) => (
            <li
              key={alert.id}
              className="rounded-2xl border border-line bg-line/40 p-3.5"
              // A warning is announced when it appears; the informational ones
              // are not, because a replay notice interrupting a screen reader
              // mid-sentence is worse than being read on arrival at the bell.
              role={alert.severity === "warning" ? "alert" : undefined}
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0">
                  {alert.severity === "warning" ? (
                    <AlertTriangle size={16} aria-hidden="true" className="text-danger-ink" />
                  ) : (
                    <Info size={16} aria-hidden="true" className="text-accent-2-ink" />
                  )}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">{alert.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-muted">{alert.message}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 border-t border-line pt-4 text-xs text-ink-muted">
        The inverter&rsquo;s own fault and warning registers are decoded but not yet stored, so they
        are not counted here. They arrive with the alarms slice.
      </p>
    </Modal>
  );
}
